import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, MapPin, Loader2, Send, Info, Sparkles, Users } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

const PAGE_SIZE = 7;

// Tags explaining why an officer was NOT matched by AI
const REASON_TAG_MAP = {
  no_location:       { label: 'Location unknown', colors: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  offline:           { label: 'Marked offline',   colors: 'bg-gray-50 text-gray-500 border-gray-200'       },
  rate_mismatch:     { label: 'Rate too high',     colors: 'bg-red-50 text-red-600 border-red-200'          },
  distance_exceeded: { label: 'Too far away',      colors: 'bg-orange-50 text-orange-700 border-orange-200' },
};

// Score breakdown items — maps solver weights to human-readable labels
const BREAKDOWN_ITEMS = [
  { key: 'proximity',  label: 'Nearby',            color: 'bg-blue-400',    max: 50  },
  { key: 'preference', label: 'Shift preference',   color: 'bg-sky-500',     max: 100 },
  { key: 'favorite',   label: 'Customer history',   color: 'bg-emerald-500', max: 120 },
  { key: 'margin',     label: 'Rate efficiency',    color: 'bg-amber-400',   max: 30  },
];

/**
 * Info icon that reveals a mini score-breakdown card on hover/click.
 * Uses position:fixed + getBoundingClientRect to escape any parent overflow:hidden.
 */
function BreakdownTooltip({ breakdown }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const updatePos = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({
      // Place the card above the icon, centred horizontally on it
      top:  r.top + window.scrollY - 8,   // 8px gap above icon
      left: r.left + window.scrollX + r.width / 2,
    });
  };

  // Recompute on scroll/resize while open so card tracks correctly
  useEffect(() => {
    if (!open) return;
    const handle = () => updatePos();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open]);

  if (!breakdown) return null;
  const hasAnyScore = BREAKDOWN_ITEMS.some(i => (breakdown[i.key] ?? 0) > 0);
  if (!hasAnyScore) return null;

  const handleOpen = () => { updatePos(); setOpen(true); };
  const handleClose = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onClick={e => { e.stopPropagation(); open ? handleClose() : handleOpen(); }}
        className="flex items-center justify-center h-4 w-4 rounded-full text-brand-pink/60 hover:text-brand-pink transition-colors"
        aria-label="Why AI recommended this officer"
      >
        <Info size={10} />
      </button>

      {open && (
        <div
          className="fixed z-[9999] w-52 rounded-xl border border-border bg-white shadow-2xl p-3 pointer-events-none"
          style={{
            // Card sits above the icon; translate(-50%, -100%) centres it horizontally and anchors bottom to icon top
            top:       pos.top,
            left:      pos.left,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {/* Arrow pointing down */}
          <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 h-2.5 w-2.5 rotate-45 border-r border-b border-border bg-white" />

          <p className="text-[10px] font-semibold text-foreground mb-2.5 flex items-center gap-1">
            <Sparkles size={9} className="text-brand-pink" />
            Why this officer was recommended
          </p>

          {BREAKDOWN_ITEMS.map(item => {
            const val = breakdown[item.key] ?? 0;
            const pct = Math.min(100, Math.round((val / item.max) * 100));
            return (
              <div key={item.key} className="mb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9px] text-muted-foreground">{item.label}</span>
                  <span className={`text-[9px] font-semibold ${pct > 0 ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                    {pct}%
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${pct > 0 ? item.color : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Single assigned-officer row inside a shift */
function AssignedRow({ offer, OFFER_STATUS, onOpenOffer, onSelectOfficer, isSelected }) {
  const user = offer.provider_resource_id?.user_id;
  const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Officer';
  const offerStatus = OFFER_STATUS[offer.status] || { label: 'Unknown', colors: 'bg-gray-50 text-gray-600 border-gray-200' };
  const sentAt = offer.createdAt ? new Date(offer.createdAt) : null;
  const expiresAt = offer.negotiations?.expiredAt ? new Date(offer.negotiations.expiredAt) : null;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
        isSelected ? 'bg-emerald-100/60' : 'hover:bg-emerald-50/60'
      }`}
      onClick={() => onSelectOfficer?.(offer)}
    >
      {/* Avatar */}
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 overflow-hidden">
        {user?.profilePictureUrl ? (
          <img src={user.profilePictureUrl} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span className="text-[9px] font-bold text-primary">{name.charAt(0).toUpperCase()}</span>
        )}
      </div>

      {/* Name + timestamps */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-card-foreground truncate">{name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {sentAt && (
            <span className="text-[10px] text-muted-foreground">
              Sent {fmtDate(sentAt)} {fmtTime(sentAt)}
            </span>
          )}
          {offer.status === 'open' && expiresAt && (
            <span className="text-[10px] text-amber-600">· Expires {fmtTime(expiresAt)}</span>
          )}
        </div>
      </div>

      {/* Rate */}
      <span className="text-[10px] font-medium text-muted-foreground flex-shrink-0">
        {offer.negotiations?.providerHourlyRate
          ? `$${offer.negotiations.providerHourlyRate.toFixed(2)}/hr`
          : '—'}
      </span>

      {/* Status badge */}
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium flex-shrink-0 ${offerStatus.colors}`}>
        {offerStatus.label}
      </span>

      {/* Show details */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpenOffer?.(offer); }}
        className="rounded-md border border-border px-2 py-1 text-[10px] font-medium text-foreground hover:bg-muted flex-shrink-0"
      >
        Details
      </button>
    </div>
  );
}

/** Paginated pool candidates for one shift */
function PoolSection({ shiftId, recs, onSelectOfficer, onSendOffer, sendingKey, selectedOfficer }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(recs.length / PAGE_SIZE);
  const pageRecs = recs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="bg-rose-50/25">
      {/* Pool sub-header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-brand-pink/5 border-t border-dashed border-brand-pink/20">
        <Sparkles size={11} className="text-brand-pink flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold text-brand-pink tracking-wide">Smart Matches</span>
          <span className="ml-1.5 text-[10px] text-muted-foreground">Recommended for you · {recs.length} officer{recs.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="divide-y divide-border">
        {pageRecs.map((rec) => {
          const resource = rec.resourceId;
          const resourceId = resource?._id || rec.resourceId;
          const key = resourceId?.toString();
          const user = resource?.user_id || {};
          const name = user.firstName
            ? `${user.firstName} ${user.lastName || ''}`.trim()
            : resource?.firstName
              ? `${resource.firstName} ${resource.lastName || ''}`.trim()
              : '—';

          const dist = rec.distanceMiles
            ? `${Math.round(rec.distanceMiles * 10) / 10} mi`
            : null;
          const officerKey = `${shiftId}-${key}`;
          const isSending = sendingKey === officerKey;
          const isSelected = selectedOfficer?.resourceId?.toString() === key;

          const handleRowClick = () => {
            const geo = resource?.geoLocation;
            const [lng, lat] = geo?.coordinates || [];
            if (!lng || !lat) return;
            const officer = {
              resourceId,
              lng, lat,
              firstName: user.firstName || resource?.firstName || '—',
              lastName: user.lastName || resource?.lastName || '',
              email: user.email,
              profilePictureUrl: user.profilePictureUrl,
              proposedRate: rec.proposedRate ?? 0,
              distanceMiles: rec.distanceMiles,
              isAssigned: false,
            };
            onSelectOfficer(isSelected ? null : officer);
          };

          return (
            <div
              key={rec._id}
              className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                isSelected ? 'bg-brand-pink/10' : 'hover:bg-rose-50/50'
              }`}
              onClick={handleRowClick}
            >
              {/* Rank */}
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                <span className="text-[9px] font-bold text-primary">#{rec.rank ?? '?'}</span>
              </div>

              {/* Avatar */}
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted overflow-hidden">
                {user.profilePictureUrl ? (
                  <img src={user.profilePictureUrl} alt={name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-[9px] font-semibold text-muted-foreground">
                    {name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-card-foreground truncate">{name}</p>
                <div className="mt-0.5 flex items-center gap-1.5">
                  {dist && (
                    <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                      <MapPin size={8} />
                      {dist}
                    </span>
                  )}
                  <span className="inline-flex items-center">
                    <BreakdownTooltip breakdown={rec.breakdown} />
                  </span>
                </div>
              </div>

              {/* Rate */}
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] font-medium text-muted-foreground">
                  ${(rec.proposedRate ?? 0).toFixed(2)}<span className="text-[9px]">/hr</span>
                </p>
              </div>

              {/* Send button */}
              <button
                type="button"
                disabled={isSending || !!sendingKey}
                onClick={(e) => { e.stopPropagation(); onSendOffer(shiftId, rec); }}
                className="flex items-center gap-1 rounded-md bg-brand-pink/10 border border-brand-pink/20 px-2.5 py-1.5 text-[10px] font-semibold text-brand-pink transition-all hover:bg-brand-pink/20 active:scale-95 disabled:opacity-40 flex-shrink-0"
              >
                {isSending ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                {isSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-2 bg-muted/10">
          <span className="text-[10px] text-muted-foreground">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, recs.length)} of {recs.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              className="flex h-6 w-6 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
            >
              <ChevronDown size={11} className="rotate-90" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-medium transition-colors ${
                  i === page
                    ? 'bg-brand-pink text-white'
                    : 'border border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages - 1}
              className="flex h-6 w-6 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
            >
              <ChevronDown size={11} className="-rotate-90" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/** Officers NOT matched — shown below Smart Matches so dispatchers can still reach them manually */
function AgencyOfficerSection({ shiftId, officers, optimizerPoolIds, onSendOffer, sendingKey, onSelectOfficer, selectedOfficer }) {
  // Filter out officers already shown in the Smart Matches (optimizer pool) section
  const filteredOfficers = officers.filter(o => !optimizerPoolIds.has(o.resourceId?.toString()));
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? filteredOfficers : [];

  if (filteredOfficers.length === 0) return null;

  return (
    <div className="bg-gray-50/40">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 border-t border-dashed border-gray-200 hover:bg-gray-100/60 transition-colors text-left"
      >
        <Users size={11} className="text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold text-muted-foreground tracking-wide">Other Officers</span>
          <span className="ml-1.5 text-[10px] text-muted-foreground">Not in recommendations · {filteredOfficers.length} available</span>
        </div>
        <ChevronDown
          size={11}
          className={`text-muted-foreground flex-shrink-0 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      <div className="divide-y divide-border">
        {visible.map((officer) => {
          const rid = officer.resourceId?.toString();
          const officerKey = `${shiftId}-${rid}`;
          const isSending = sendingKey === officerKey;
          const name = `${officer.firstName} ${officer.lastName}`.trim() || '—';
          const dist = officer.distanceMiles != null
            ? `${Math.round(officer.distanceMiles * 10) / 10} mi`
            : null;

          // Build a rec-compatible object for onSendOffer
          const recCompat = {
            resourceId: {
              _id: officer.resourceId,
              user_id: { firstName: officer.firstName, lastName: officer.lastName },
              geoLocation: officer.geoLocation,
            },
            proposedRate: officer.minHourlyRate || 0,
          };

          return (
            <div
              key={rid}
              className={`flex items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer ${
                selectedOfficer?.resourceId?.toString() === rid ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
              onClick={() => {
                if (!officer.geoLocation?.coordinates?.[0]) return;
                const [lng, lat] = officer.geoLocation.coordinates;
                const mapOfficer = {
                  resourceId: officer.resourceId,
                  lng, lat,
                  firstName: officer.firstName,
                  lastName: officer.lastName,
                  email: officer.email,
                  profilePictureUrl: officer.profilePictureUrl,
                  proposedRate: officer.minHourlyRate || 0,
                  distanceMiles: officer.distanceMiles,
                  isAssigned: false,
                  isOther: true,
                };
                onSelectOfficer?.(selectedOfficer?.resourceId?.toString() === rid ? null : mapOfficer);
              }}
            >
              {/* Avatar */}
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted overflow-hidden">
                {officer.profilePictureUrl ? (
                  <img src={officer.profilePictureUrl} alt={name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-[9px] font-semibold text-muted-foreground">
                    {name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Name + tags */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-card-foreground truncate">{name}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  {dist && (
                    <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                      <MapPin size={8} />{dist}
                    </span>
                  )}
                  {officer.reasons.map(r => {
                    const tag = REASON_TAG_MAP[r];
                    return tag ? (
                      <span key={r} className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-medium ${tag.colors}`}>
                        {tag.label}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Min rate */}
              {officer.minHourlyRate > 0 && (
                <span className="text-[10px] text-muted-foreground flex-shrink-0">
                  ${officer.minHourlyRate.toFixed(2)}/hr min
                </span>
              )}

              {/* Send button */}
              <button
                type="button"
                disabled={isSending || !!sendingKey}
                onClick={(e) => { e.stopPropagation(); onSendOffer(shiftId, recCompat); }}
                className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground transition-all hover:bg-muted active:scale-95 disabled:opacity-40 flex-shrink-0"
              >
                {isSending ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                {isSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          );
        })}
      </div>

      {expanded && filteredOfficers.length > 10 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="w-full py-2 text-[10px] text-muted-foreground hover:text-foreground transition-colors border-t border-border"
        >
          Collapse ↑
        </button>
      )}
    </div>
  );
}

/**
 * ShiftAllocationTable
 *
 * Single accordion card. Shifts are the primary rows.
 * Each shift expands to show:
 *   A) Assigned officers (always, if any)
 *   B) Optimizer pool candidates (only when shift is NOT at capacity)
 *
 * Full shifts default to collapsed. Incomplete shifts default to open.
 */
const ShiftAllocationTable = ({
  shifts,
  offersByShift,
  poolByShift,
  proximityPool,
  assignedOfficerIds,
  OFFER_STATUS,
  ACTIVE_OFFER_STATUSES,
  onOpenOffer,
  onSelectAssignedOfficer,
  onSelectOfficer,
  onSendOffer,
  sendingKey,
  selectedOfficer,
}) => {
  // Compute per-shift capacity up front
  const shiftMeta = useMemo(() => {
    const now = new Date();
    return shifts.map((shift) => {
      const shiftId = shift._id?.toString();
      const offers = offersByShift[shiftId] || [];
      const activeOffers = offers.filter((o) => ACTIVE_OFFER_STATUSES.includes(o.status));
      const assignedCount = activeOffers.length;
      const staffsNeeded = shift.staffsRequired || 1;
      const isFull = assignedCount >= staffsNeeded;
      const isPast = shift.endTime ? new Date(shift.endTime) < now : false;
      const pendingPool = (poolByShift?.[shiftId] || []).filter(
        (r) => r.status === 'pending' &&
          !assignedOfficerIds.has((r.resourceId?._id || r.resourceId)?.toString())
      );
      // Set of resourceIds already in the optimizer pool for this shift (for tagging)
      const optimizerPoolIds = new Set(
        pendingPool.map(r => (r.resourceId?._id || r.resourceId)?.toString())
      );
      return { shift, shiftId, activeOffers, assignedCount, staffsNeeded, isFull, isPast, pendingPool, optimizerPoolIds };
    });
  }, [shifts, offersByShift, poolByShift, assignedOfficerIds, ACTIVE_OFFER_STATUSES]);

  // Full shifts collapsed by default; incomplete shifts open
  const [openShifts, setOpenShifts] = useState(() => {
    const init = {};
    shiftMeta.forEach(({ shiftId, isFull }) => {
      init[shiftId] = !isFull;
    });
    return init;
  });

  const toggleShift = (shiftId) =>
    setOpenShifts((prev) => ({ ...prev, [shiftId]: !prev[shiftId] }));

  const durationStr = (shift) => {
    const min = shift.endTime && shift.startTime
      ? Math.round((new Date(shift.endTime) - new Date(shift.startTime)) / 60000)
      : 0;
    return min === 0 ? '—' : `${Math.floor(min / 60)}h ${min % 60}m`;
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      {/* Table header */}
      <div className="bg-muted/20 border-b border-border px-4 py-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Shifts ({shifts.length})
        </p>
      </div>

      <div className="divide-y divide-border">
        {shiftMeta.map(({ shift, shiftId, activeOffers, assignedCount, staffsNeeded, isFull, isPast, pendingPool, optimizerPoolIds }, idx) => {
          const isOpen = openShifts[shiftId] ?? !isFull;
          const hasAssigned = activeOffers.length > 0;
          const hasPool = pendingPool.length > 0;
          const hasProximityPool = (proximityPool || []).length > 0;

          return (
            <div key={shiftId}>
              {/* ── Shift header row ── */}
              <button
                type="button"
                onClick={() => toggleShift(shiftId)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors bg-brand-blue-dark/5 hover:bg-brand-blue-dark/10 ${
                  isFull || isPast ? 'opacity-70' : ''
                }`}
              >
                {/* Shift number */}
                <span className="text-[10px] font-bold text-muted-foreground flex-shrink-0 w-10">
                  Shift {idx + 1}
                </span>

                {/* Date · Time */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-card-foreground">
                    {fmtDate(shift.startTime)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {fmtTime(shift.startTime)} – {fmtTime(shift.endTime)} · {durationStr(shift)}
                  </p>
                </div>

                {/* Capacity badge */}
                <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold flex-shrink-0 ${
                  isPast
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : isFull
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : assignedCount > 0
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {isPast ? 'Past' : isFull ? '●' : assignedCount > 0 ? '◐' : '○'}{!isPast && ` ${assignedCount}/${staffsNeeded}`}
                </span>

                {/* Chevron */}
                {isOpen
                  ? <ChevronUp size={13} className="text-muted-foreground flex-shrink-0" />
                  : <ChevronDown size={13} className="text-muted-foreground flex-shrink-0" />}
              </button>

              {/* ── Expanded panel ── */}
              {isOpen && (
                <div className="border-t border-border/60 bg-background/50">
                  {/* A) Assigned officers */}
                  {hasAssigned && (
                    <div className="bg-emerald-50/30">
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-emerald-50/60">
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">
                          Assigned
                        </span>
                        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-bold text-white leading-none">
                          {activeOffers.length}
                        </span>
                      </div>
                      <div className="divide-y divide-border">
                        {activeOffers.map((offer) => (
                          <AssignedRow
                            key={offer._id}
                            offer={offer}
                            OFFER_STATUS={OFFER_STATUS}
                            onOpenOffer={onOpenOffer}
                            onSelectOfficer={onSelectAssignedOfficer}
                            isSelected={
                              selectedOfficer?.offerId?.toString() === offer?._id?.toString() ||
                              selectedOfficer?.resourceId?.toString() ===
                                (offer?.provider_resource_id?._id || offer?.provider_resource_id)?.toString()
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* B) Smart Matches — only when shift is NOT full AND not in the past */}
                  {!isFull && !isPast && hasPool && (
                    <PoolSection
                      shiftId={shiftId}
                      recs={pendingPool}
                      onSelectOfficer={onSelectOfficer}
                      onSendOffer={onSendOffer}
                      sendingKey={sendingKey}
                      selectedOfficer={selectedOfficer}
                    />
                  )}

                  {/* Fully staffed notice */}
                  {isFull && !isPast && (
                    <div className="px-4 py-3 flex items-center gap-2 text-[11px] text-emerald-700">
                      <span className="text-emerald-500">●</span>
                      Shift fully staffed — no offers needed
                    </div>
                  )}

                  {/* Past shift notice */}
                  {isPast && (
                    <div className="px-4 py-3 flex items-center gap-2 text-[11px] text-gray-400">
                      <span>●</span>
                      This shift has already ended — offers can no longer be sent
                    </div>
                  )}

                  {/* C) Other Officers — only when not full AND not in the past */}
                  {!isFull && !isPast && hasProximityPool && (
                    <AgencyOfficerSection
                      shiftId={shiftId}
                      officers={proximityPool}
                      optimizerPoolIds={optimizerPoolIds}
                      onSendOffer={onSendOffer}
                      sendingKey={sendingKey}
                      onSelectOfficer={onSelectOfficer}
                      selectedOfficer={selectedOfficer}
                    />
                  )}

                  {/* Fallback: no pool and no proximity officers */}
                  {!isFull && !hasPool && !hasProximityPool && (
                    <div className="px-4 py-3 text-[11px] text-muted-foreground italic">
                      No officers available yet — add officers from the Officers tab
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShiftAllocationTable;
